import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPatch, apiPost } from "./api";

const MAX_CRITERIA = 10;
const YEARS = Array.from({ length: 16 }, (_, i) => String(new Date().getFullYear() - i));
const FUELS = ["", "Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique", "GPL"];
const COUNTRIES = ["", "France", "Allemagne", "Belgique", "Pays-Bas", "Espagne", "Italie", "Pologne"];
const EQUIPMENT_OPTIONS = [
  ["carplay", "Apple CarPlay / Android Auto"],
  ["camera360", "Caméra 360°"],
  ["adaptive_cruise", "Régulateur adaptatif"],
  ["panoramic_roof", "Toit panoramique"],
  ["heated_seats", "Sièges chauffants"],
  ["leather", "Sellerie cuir"],
  ["matrix_led", "Phares Matrix / LED"],
  ["premium_audio", "Audio premium"],
  ["towbar", "Attelage"],
  ["awd", "Transmission intégrale"],
  ["sport_pack", "Pack sport"],
  ["headup", "Affichage tête haute"],
  ["keyless", "Accès / démarrage sans clé"],
  ["electric_seats", "Sièges électriques"],
];
const EQUIPMENT_LABELS = Object.fromEntries(EQUIPMENT_OPTIONS);
const SOURCE_GROUPS = [
  ["Multi-pays 🇪🇺", [["autoscout24","AutoScout24"]]],
  ["France 🇫🇷", [["leboncoin","Leboncoin"], ["lacentrale","La Centrale"], ["paruvendu","ParuVendu"], ["facebook","Facebook Marketplace"]]],
  ["Allemagne 🇩🇪", [["mobile","mobile.de / automobile.fr"], ["kleinanzeigen","Kleinanzeigen"]]],
  ["Belgique / Pays-Bas 🇧🇪 🇳🇱", [["tweedehands","2dehands"], ["marktplaats","Marktplaats"], ["autotrack","AutoTrack"]]],
  ["Espagne / Italie / Pologne 🇪🇸 🇮🇹 🇵🇱", [
    ["coches","Coches.net"], ["milanuncios","Milanuncios"], ["subito","Subito.it"], ["automobile_it","Automobile.it"], ["otomoto","Otomoto"], ["olx_pl","OLX.pl"]
  ]],
  ["Enchères & remarketing", [
    ["vpauto","VPauto PRO · enchères"], ["bca","BCA"], ["alcopa","Alcopa Auction"], ["autorola","Autorola"], ["fiveauction","Five Auction"], ["interencheres","Interencheres"]
  ]],
];
const SOURCE_LABELS = Object.fromEntries(SOURCE_GROUPS.flatMap(([,items])=>items));

const EMPTY = () => ({
  make:"", model:"", fuel:"", year_min:"", year_max:"", mileage_max:"", price_max:"", country:"",
  source_type:"manual", source_lead_id:null, source_lead_ids:[], source_client_name:"", source_client_email:"", source_client_phone:"", source_created_at:"", last_sourced_at:null, last_sourced_count:0, preferred_sources:[], options:[],
});

function CriterionRow({ index, value, onChange, onRemove, canRemove, makes }) {
  const [models, setModels] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!value.make) { setModels([]); return () => { alive = false; }; }
    apiGet(`/api/catalogue/models?brand=${encodeURIComponent(value.make)}`).then(r => { if (alive) setModels(r?.models || []); }).catch(() => { if (alive) setModels([]); });
    return () => { alive = false; };
  }, [value.make]);
  const set = (k, v) => onChange({ ...value, [k]: v, ...(k === "make" ? { model:"" } : {}) });
  const fromLead = value.source_type === "lead" || !!value.source_lead_id;
  return (
    <div className={`viq-scan-criterion${fromLead ? " from-lead" : ""}`} data-lead-id={value.source_lead_id || undefined}>
      <div className="viq-scan-criterion-head">
        <div className="viq-scan-profile-title">
          <strong>Profil {index + 1}</strong>
          {fromLead && <span className="viq-scan-lead-badge">Source lead{(value.source_lead_ids?.length || 0) > 1 ? ` · ${value.source_lead_ids.length} demandes` : ""}</span>}
          {value.last_sourced_at ? <span className="viq-scan-status-badge sourced">✓ Sourcé · {new Date(value.last_sourced_at).toLocaleString("fr-FR", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}</span> : <span className="viq-scan-status-badge pending">À sourcer</span>}
        </div>
        {canRemove && <button type="button" className="viq-scan-remove" onClick={onRemove}>Retirer</button>}
      </div>
      {fromLead && (
        <div className="viq-scan-lead-source">
          <span><b>{value.source_client_name || "Client"}</b>{value.source_lead_id ? ` · Lead #${value.source_lead_id}` : ""}</span>
          <span>{[value.source_client_email, value.source_client_phone].filter(Boolean).join(" · ") || "Coordonnées disponibles dans le CRM"}</span>
        </div>
      )}
      <details className="viq-source-picker">
        <summary><span>Plateformes prioritaires</span><strong>{(value.preferred_sources||[]).length ? (value.preferred_sources||[]).map(id=>SOURCE_LABELS[id]||id).slice(0,2).join(", ") + ((value.preferred_sources||[]).length>2 ? ` +${(value.preferred_sources||[]).length-2}` : "") : "Toutes les sources pertinentes"}</strong></summary>
        <div className="viq-source-picker-menu">
          <div className="viq-source-picker-note">Sélection multiple. Les plateformes choisies sont interrogées en priorité, puis VIQ.AI complète uniquement si nécessaire avec les sources les plus pertinentes du pays.</div>
          {SOURCE_GROUPS.map(([group, items]) => <fieldset key={group}><legend>{group}</legend>{items.map(([id,label]) => { const checked=(value.preferred_sources||[]).includes(id); return <label key={id}><input type="checkbox" checked={checked} onChange={() => set("preferred_sources", checked ? (value.preferred_sources||[]).filter(x=>x!==id) : [...(value.preferred_sources||[]),id])}/><span>{label}</span></label>; })}</fieldset>)}
          {!!(value.preferred_sources||[]).length && <button type="button" className="viq-source-clear" onClick={()=>set("preferred_sources",[])}>Réinitialiser la priorité</button>}
        </div>
      </details>
      <div className="viq-scan-grid">
        <label>Marque
          <select value={value.make || ""} onChange={e => set("make", e.target.value)}>
            <option value="">Toutes</option>{makes.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>Modèle
          <select value={value.model || ""} disabled={!value.make} onChange={e => set("model", e.target.value)}>
            <option value="">Tous</option>{models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>Énergie
          <select value={value.fuel || ""} onChange={e => set("fuel", e.target.value)}>{FUELS.map(f => <option key={f || "all"} value={f}>{f || "Toutes"}</option>)}</select>
        </label>
        <label>Année min.
          <select value={value.year_min || ""} onChange={e => set("year_min", e.target.value)}><option value="">—</option>{YEARS.map(y => <option key={y}>{y}</option>)}</select>
        </label>
        <label>Année max.
          <select value={value.year_max || ""} onChange={e => set("year_max", e.target.value)}><option value="">—</option>{YEARS.map(y => <option key={y}>{y}</option>)}</select>
        </label>
        <label>Pays source
          <select value={value.country || ""} onChange={e => set("country", e.target.value)}>{COUNTRIES.map(c => <option key={c || "all"} value={c}>{c || "Tous pays"}</option>)}</select>
        </label>
        <label>Km max.
          <input type="number" min="0" step="5000" value={value.mileage_max || ""} placeholder="ex. 80000" onChange={e => set("mileage_max", e.target.value)} />
        </label>
        <label>Budget achat max.
          <input type="number" min="0" step="500" value={value.price_max || ""} placeholder="ex. 30000" onChange={e => set("price_max", e.target.value)} />
        </label>
        <details className="viq-options-picker">
          <summary><span>Options / équipements</span><strong>{(value.options||[]).length ? `${(value.options||[]).slice(0,2).map(id=>EQUIPMENT_LABELS[id]||id).join(", ")}${(value.options||[]).length>2?` +${(value.options||[]).length-2}`:""}` : "Indifférent"}</strong></summary>
          <div className="viq-options-picker-menu">
            <div className="viq-source-picker-note">Sélection multiple. Les équipements choisis renforcent la requête et le score de pertinence sans bloquer une annonce si la plateforme ne publie pas tous ses équipements.</div>
            {EQUIPMENT_OPTIONS.map(([id,label]) => { const checked=(value.options||[]).includes(id); return <label key={id}><input type="checkbox" checked={checked} onChange={()=>set("options", checked ? (value.options||[]).filter(x=>x!==id) : [...(value.options||[]),id])}/><span>{label}</span></label>; })}
            {!!(value.options||[]).length && <button type="button" className="viq-source-clear" onClick={()=>set("options",[])}>Effacer les options</button>}
          </div>
        </details>
      </div>
    </div>
  );
}

export default function OpportunityScanModal({ open, onClose, onChanged, onShowResults, focusLeadId = null }) {
  const [criteria, setCriteria] = useState([EMPTY()]);
  const [detectedCount, setDetectedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [catalog, setCatalog] = useState({ makes:[] });
  const [queueCount, setQueueCount] = useState(0);
  const [queueItems, setQueueItems] = useState([]);
  const [scanElapsed, setScanElapsed] = useState(0);
  const [autoSourcing, setAutoSourcing] = useState({ enabled:false, coming_soon:true, trigger:"daily", threshold:3, time:"08:00" });
  const [closing, setClosing] = useState(false);
  const requestClose = () => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(() => { setClosing(false); onClose?.(); }, 150);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    let alive = true;
    Promise.all([
      apiGet("/api/opportunities/preferences").catch(() => null),
      apiGet("/api/opportunities/summary").catch(() => null),
      apiGet("/api/catalogue/brands").catch(() => null),
    ]).then(([p, o, cat]) => {
      if (!alive) return;
      const pref = p?.preferences || p || {};
      const saved = Array.isArray(pref.criteria) && pref.criteria.length ? pref.criteria.slice(0, MAX_CRITERIA) : [];
      setCriteria(saved.length ? saved.map(x => ({ ...EMPTY(), ...x })) : [EMPTY()]);
      setQueueCount(Number(pref.queue_count || 0));
      setQueueItems(Array.isArray(pref.queue) ? pref.queue : []);
      setAutoSourcing({ enabled:false, coming_soon:true, trigger:"daily", threshold:3, time:"08:00", ...(pref.auto_sourcing||{}) });
      setDetectedCount(Number(o?.count || 0));
      setCatalog({ makes: Array.isArray(cat?.brands) ? cat.brands : [] });
    });
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!open || !focusLeadId) return;
    const timer = setTimeout(() => {
      const wanted = Number(focusLeadId);
      const direct = document.querySelector(`[data-lead-id="${wanted}"]`);
      const merged = [...document.querySelectorAll("[data-source-lead-ids]")].find(node => String(node.dataset.sourceLeadIds || "").split(",").map(Number).includes(wanted));
      const el = direct || merged;
      if (el) {
        try { el.scrollIntoView({ behavior:"smooth", block:"center" }); } catch (_) {}
        el.classList.add("focus-lead");
        setTimeout(() => el.classList.remove("focus-lead"), 2200);
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [open, focusLeadId, criteria.length]);

  useEffect(() => {
    if (!open) return;
    const esc = e => { if (e.key === "Escape") requestClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, closing]);

  useEffect(() => {
    if (!loading) { setScanElapsed(0); return; }
    const started = Date.now();
    const t = window.setInterval(() => setScanElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(t);
  }, [loading]);

  const cleanCriteria = useMemo(() => criteria.map(c => ({
    make:String(c.make||"").trim(), model:String(c.model||"").trim(), fuel:String(c.fuel||"").trim(), country:String(c.country||"").trim(),
    year_min:c.year_min ? Number(c.year_min) : null, year_max:c.year_max ? Number(c.year_max) : null,
    mileage_max:c.mileage_max ? Number(c.mileage_max) : null, price_max:c.price_max ? Number(c.price_max) : null,
    source_type:c.source_type === "lead" ? "lead" : "manual",
    source_lead_id:c.source_lead_id ? Number(c.source_lead_id) : null,
    source_lead_ids:Array.isArray(c.source_lead_ids) ? c.source_lead_ids.map(Number).filter(Boolean) : (c.source_lead_id ? [Number(c.source_lead_id)] : []),
    source_client_name:String(c.source_client_name||""), source_client_email:String(c.source_client_email||""), source_client_phone:String(c.source_client_phone||""),
    source_created_at:String(c.source_created_at||""), last_sourced_at:c.last_sourced_at||null, last_sourced_count:Number(c.last_sourced_count||0),
    preferred_sources:Array.isArray(c.preferred_sources)?c.preferred_sources.map(String).filter(Boolean):[],
    options:Array.isArray(c.options)?c.options.map(String).filter(Boolean):[],
  })).filter(c => c.make || c.model || c.fuel || c.country || c.year_min || c.year_max || c.mileage_max || c.price_max || (c.options||[]).length).slice(0, MAX_CRITERIA), [criteria]);

  if (!open) return null;

  const save = async () => {
    setSaving(true); setError("");
    try {
      const r = await apiPatch("/api/opportunities/preferences", { criteria: cleanCriteria, auto_sourcing: autoSourcing });
      if (r?.success === false) throw new Error(r.error || "Enregistrement impossible");
      if (r?.preferences) { setQueueCount(Number(r.preferences.queue_count || 0)); setCriteria((r.preferences.criteria || cleanCriteria).map(x => ({...EMPTY(), ...x}))); }
      const refreshed = await apiGet("/api/opportunities/preferences").catch(() => null);
      if (refreshed?.preferences) { setQueueCount(Number(refreshed.preferences.queue_count || 0)); setQueueItems(Array.isArray(refreshed.preferences.queue) ? refreshed.preferences.queue : []); }
      onChanged?.();
    } catch (e) { setError(e.message || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const scan = async () => {
    setLoading(true); setError("");
    try {
      await apiPatch("/api/opportunities/preferences", { criteria: cleanCriteria, auto_sourcing: autoSourcing });
      const r = await apiPost("/api/opportunities/scan", { force:true });
      if (r?.success === false) throw new Error(r.error || "Sourcing impossible");
      setDetectedCount(Number(r?.detected || r?.count || 0));
      if (r?.preferences?.criteria) { setCriteria(r.preferences.criteria.map(x => ({...EMPTY(), ...x}))); setQueueCount(Number(r.preferences.queue_count || 0)); }
      const refreshed = await apiGet("/api/opportunities/preferences").catch(() => null);
      if (refreshed?.preferences) { setCriteria((refreshed.preferences.criteria || []).map(x => ({...EMPTY(), ...x}))); setQueueCount(Number(refreshed.preferences.queue_count || 0)); setQueueItems(Array.isArray(refreshed.preferences.queue) ? refreshed.preferences.queue : []); }
      onChanged?.();
      window.dispatchEvent(new Event("viq-opportunities-changed"));
    } catch (e) { setError(e.message || "Sourcing impossible"); }
    finally { setLoading(false); }
  };

  return (
    <div className={`viq-scan-overlay${closing ? " is-closing" : ""}`} onMouseDown={e => { if (e.target === e.currentTarget) requestClose(); }}>
      <section className="viq-scan-modal" role="dialog" aria-modal="true" aria-label="VIQ.AI Sourcing multiplateformes">
        <header className="viq-scan-modal-head">
          <div><span>VIQ.AI SOURCING</span><h2>VIQ.AI Sourcing multiplateformes</h2><p>Définissez jusqu'à 10 profils. VIQ.AI recherche des annonces publiques et des ventes aux enchères, puis les rapproche automatiquement de vos besoins de sourcing et des demandes d'achat reçues dans le CRM.</p></div>
          <button type="button" onClick={requestClose} aria-label="Fermer">×</button>
        </header>
        <div className="viq-scan-modal-body">
          <div className="viq-scan-toolbar">
            <div><strong>{criteria.length}/10 profils actifs{queueCount > 0 ? ` · ${queueCount} en file d’attente` : ""}</strong><span>Les demandes identiques (marque, modèle, année, motorisation) sont regroupées dans un seul profil. Au-delà de 10 profils, les nouvelles demandes passent automatiquement en file d’attente.</span></div>
            <button type="button" disabled={criteria.length >= MAX_CRITERIA} onClick={() => setCriteria(v => [...v, EMPTY()])}>+ Ajouter un profil</button>
          </div>
          <div className="viq-scan-criteria-list">
            {criteria.map((c, i) => <CriterionRow key={`${c.source_lead_id || "manual"}-${i}`} index={i} value={c} makes={catalog.makes}
              canRemove={criteria.length > 1} onRemove={() => setCriteria(v => v.filter((_,j) => j !== i))}
              onChange={next => setCriteria(v => v.map((x,j) => j === i ? next : x))} />)}
          </div>
          <details className="viq-auto-sourcing">
            <summary><span><strong>Auto-sourcing</strong><em>Bientôt</em></span><small>Préparez dès maintenant les règles de déclenchement automatique.</small></summary>
            <div className="viq-auto-sourcing-body">
              <div className="viq-auto-sourcing-lock">L’exécution automatique sera activée dans une prochaine version. Vos réglages sont enregistrés dès maintenant.</div>
              <label><span>Déclenchement souhaité</span><select value={autoSourcing.trigger||"daily"} onChange={e=>setAutoSourcing(v=>({...v,trigger:e.target.value}))}><option value="daily">Tous les jours</option><option value="new_purchase_lead">À chaque nouvelle demande d'achat</option><option value="threshold">À partir de X nouvelles demandes d'achat</option></select></label>
              {autoSourcing.trigger==="daily" && <label><span>Heure préférée</span><input type="time" value={autoSourcing.time||"08:00"} onChange={e=>setAutoSourcing(v=>({...v,time:e.target.value}))}/></label>}
              {autoSourcing.trigger==="threshold" && <label><span>Seuil de nouvelles demandes</span><input type="number" min="1" max="50" value={autoSourcing.threshold||3} onChange={e=>setAutoSourcing(v=>({...v,threshold:Math.max(1,Number(e.target.value)||1)}))}/></label>}
            </div>
          </details>
          {queueItems.length > 0 && <div className="viq-scan-queue" aria-label="File d’attente sourcing">
            <div className="viq-scan-queue-head"><strong>File d’attente · {queueItems.length}</strong><span>Ces demandes seront promues automatiquement dès qu’un emplacement de profil se libère.</span></div>
            <div className="viq-scan-queue-list">{queueItems.slice(0,20).map((q,i)=>{ const c=q.criterion||{}; return <div key={q.id||`${q.lead_id}-${i}`} className="viq-scan-queue-item" data-lead-id={q.lead_id||undefined} data-source-lead-ids={(c.source_lead_ids||[q.lead_id]).filter(Boolean).join(",")}><span>#{i+1}</span><strong>{[c.make,c.model,c.year_min||c.year_max,c.fuel].filter(Boolean).join(" · ") || "Demande d’achat"}{(c.source_lead_ids?.length||0)>1?` · ${c.source_lead_ids.length} demandes`:""}</strong><small>{c.source_client_name || `Lead #${q.lead_id}`}{q.created_at?` · ${new Date(q.created_at).toLocaleString("fr-FR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"})}`:""}</small></div>; })}</div>
          </div>}
          {loading && <div className="viq-sourcing-progress" role="status" aria-live="polite">
            <div className="viq-sourcing-progress-head"><strong>VIQ.AI explore les sources automobiles</strong><span>{scanElapsed}s écoulées</span></div>
            <div className="viq-sourcing-progress-track"><span style={{width:`${Math.min(92, 18 + scanElapsed * 2.2)}%`}} /></div>
            <div className="viq-sourcing-progress-copy">{scanElapsed < 12 ? "Qualification des profils et interrogation des sources pertinentes…" : scanElapsed < 28 ? "Comparaison, déduplication et enrichissement des annonces…" : "La recherche prend un peu plus de temps car plusieurs sources sont consultées. Vous pouvez continuer : les résultats s’afficheront automatiquement dès que le sourcing est terminé."}</div>
          </div>}
          {error && <div className="viq-scan-error">{error}</div>}
          <div className="viq-scan-cta-row">
            <button type="button" className="secondary" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer les profils"}</button>
            <button type="button" className="secondary viq-scan-show-results" onClick={() => { onShowResults?.(); requestClose(); }}>Afficher les résultats{detectedCount > 0 ? ` (${detectedCount})` : ""}</button>
            <button type="button" className="primary" onClick={scan} disabled={loading}>{loading ? "Sourcing en cours…" : "Lancer le sourcing"}</button>
          </div>
          <div className="viq-scan-config-note">Cette fenêtre sert uniquement à configurer le sourcing. Les résultats sont centralisés dans <strong>Dashboard Pro → VIQ.AI Sourcing</strong> et <strong>Dossiers VIQ → VIQ.AI Sourcing</strong>.</div>
        </div>
      </section>
    </div>
  );
}
